import React, { useEffect, useState, useMemo } from 'react';
import { useTheme } from '@emotion/react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import axios from 'axios';
import {
  Button,
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Typography,
  Chip,
  Grid
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';
// import VideocamIcon from '@mui/icons-material/Videocam';
// import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import SettingsIcon from '@mui/icons-material/Settings';
import SideBar from '../sideBar';
import AddCameraButton from './components/AddCameraButton';
import Modal from '@mui/material/Modal';
import ImageAnnotationTool from './ImageAnnotationTool';
import defaultImage from '../../../../assets/images/Camera-013_pathway(entry-samsung)_mobile-zone.jpg';
import MiddleController from './components/MiddleController';

const token = async () => {
  return localStorage.getItem('token');
};

// API function to get all cameras
const getCameras = async () => {
  try {
    // Get source from localStorage and include it in the request body
    const source = JSON.parse(localStorage.getItem('source'));

    const res = await axios.post(
      'https://db.neophyte.live/sentinel-api/api/sentinel/getAllCameras',
      { source },
      {
        headers: {
          Accept: 'application/json',
          Authorization: await token()
        }
      }
    );
    return res;
  } catch (error) {
    console.log('Error while calling cameras API:', error);
    return { data: { cameras: [] } };
  }
};

// Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, disabled = false }) => {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} disabled={disabled} />
      <div
        className={`
        w-11 h-6 rounded-full peer
        ${checked ? 'bg-green-500' : 'bg-gray-400'}
        peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-200
        transition-all duration-200 ease-in-out
      `}
      >
        <div
          className={`
          absolute top-[2px] left-[2px]
          bg-white border-gray-300 rounded-full
          h-5 w-5 transition-all duration-200
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `}
        />
      </div>
    </label>
  );
};

const CameraPage = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAnnotate, setOpenAnnotate] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [cameraImage, setCameraImage] = useState(defaultImage);
  const [setLoadingImage] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteCameraId, setDeleteCameraId] = useState(null);
  const [deletionInProgress, setDeletionInProgress] = useState(false);
  const [deletionSuccess, setDeletionSuccess] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [storeName, setStoreName] = useState('Camera Management');

  // Fetch cameras data
  const fetchCameras = async () => {
    setLoading(true);
    try {
      const response = await getCameras();
      const allCameras = response?.data?.cameras || [];
      console.log('API Response:', allCameras); // For debugging
      setData(allCameras);
    } catch (error) {
      console.error('Error fetching cameras:', error);
      setApiError('Failed to fetch cameras');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAnnotate = () => {
    setOpenAnnotate(false);
    setSelectedDevice(null);
  };

  useEffect(() => {
    fetchCameras();
  }, []);

  const mainContentStyle = {
    maxWidth: '100%',
    minWidth: '0',
    height: '100%',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      width: '100%'
    }
  };

  // Get store name from localStorage if available
  useEffect(() => {
    const storedName = localStorage.getItem('storeName');
    if (storedName) {
      setStoreName(storedName);
    } else {
      setStoreName('Store Insight'); // Default fallback
    }
  }, []);

  // Handle edit camera
  const handleEditCamera = (camera) => {
    console.log('Edit camera:', camera);
    // Implement edit functionality here
  };

  // Handle delete confirmation dialog open
  const handleConfirmDialogOpen = (cameraId) => {
    setDeleteCameraId(cameraId);
    setConfirmDialogOpen(true);
  };

  // Handle delete confirmation dialog close
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const toggleCameraStatus = async (cameraId) => {
    try {
      setLoading(true);
      const response = await axios.post(
        'http://localhost:7071/api/sentinel/cameraStatus',
        { cameraId },
        {
          headers: {
            Accept: 'application/json',
            Authorization: await token()
          }
        }
      );

      if (response.data.message === 'Successfully updated the camera status') {
        // Update the local state to reflect the change
        setData((prevData) => prevData.map((camera) => (camera._id === cameraId ? { ...camera, active: response.data.active } : camera)));
        setDeletionSuccess('Camera status updated successfully');
      }
    } catch (error) {
      console.error('Error toggling camera status:', error);
      setApiError('Failed to update camera status');
    } finally {
      setLoading(false);
    }
  };

  // Handle camera deletion
  const handleCameraDeletion = async () => {
    if (!deleteCameraId) return;

    setDeletionInProgress(true);
    try {
      console.log('Deleting camera with ID:', deleteCameraId);
      setData((prevData) => prevData.filter((camera) => camera._id !== deleteCameraId));
      setDeletionSuccess('Camera deleted successfully');
      setConfirmDialogOpen(false);
    } catch (error) {
      console.error('Error deleting camera:', error);
      setApiError('Failed to delete camera');
    } finally {
      setDeletionInProgress(false);
    }
  };

  const handleAIButtonClick = async (cameraName) => {
    try {
      setLoadingImage(true);
      setSelectedDevice(cameraName);
      setCameraImage(defaultImage);
    } catch (error) {
      console.error('Error fetching camera frame:', error);
      setCameraImage(defaultImage);
    } finally {
      setLoadingImage(false);
      setOpenAnnotate(true);
    }
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'index',
        header: 'Sl No.',
        size: 80,
        Cell: ({ row }) => <span>{row.index + 1}</span>
      },
      {
        accessorKey: 'device_name',
        header: 'Name',
        size: 150
      },
      {
        accessorKey: 'camera_address',
        header: 'Address',
        size: 300,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()}>
            <Typography noWrap style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {cell.getValue()}
            </Typography>
          </Tooltip>
        )
      },
      {
        accessorKey: 'active',
        header: 'Status',
        size: 100,
        Cell: ({ cell, row }) => (
          <Tooltip title={cell.getValue() ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}
              onClick={() => toggleCameraStatus(row.original._id, cell.getValue())}
            >
              <ToggleSwitch
                checked={cell.getValue()}
                onChange={() => toggleCameraStatus(row.original._id, cell.getValue())}
                disabled={loading}
              />
              <Typography
                variant="body2"
                style={{
                  color: cell.getValue() ? '#4CAF50' : '#F44336',
                  fontWeight: 500
                }}
              >
                {cell.getValue() ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </Tooltip>
        )
      },
      {
        accessorKey: 'ai',
        header: 'AI',
        size: 100,
        Cell: ({ row }) => (
          <Chip
            label="Configure"
            deleteIcon={<SettingsIcon />}
            onDelete={() => {}}
            onClick={() => handleAIButtonClick(row.original.device_name, row.original.camera_address)}
            sx={{
              backgroundColor: 'transparent',
              color: theme.palette.primary.main,
              cursor: 'pointer',
              fontWeight: 500,
              borderRadius: '16px',
              border: `1px solid ${theme.palette.primary.main}`,
              '& .MuiChip-deleteIcon': {
                color: theme.palette.primary.main,
                marginRight: '4px',
                marginLeft: '4px'
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
            size="small"
          />
        )
      }
    ],
    [theme.palette.primary.main, loading]
  );

  // Configure the table
  const table = useMaterialReactTable({
    columns,
    data,
    enableMultiRowSelection: true,
    enableRowSelection: true,
    enableSelectAll: true,
    enableSubRowSelection: true,
    enableColumnOrdering: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    enableFilters: true,
    initialState: { density: 'compact', pageSize: 10, pageIndex: 0 },
    onPaginationChange: setPagination,
    manualPagination: false,
    rowCount: data.length,
    manualFiltering: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: { globalFilter, pagination, isLoading: loading, sorting },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
        fontSize: '13px',
        color: 'white',
        backgroundColor: '#44BE54',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '12px 16px',
        '&:first-of-type': {
          borderTopLeftRadius: '8px'
        },
        '&:last-child': {
          borderTopRightRadius: '8px'
        }
      }
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        background: '#ffff',
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #f0f0f0'
      }
    },
    muiTableContainerProps: {
      sx: {
        backgroundColor: '#fff',
        minHeight: '500px'
      }
    },
    muiTableBodyCellProps: {
      sx: {
        background: '#ffff',
        padding: '8px 16px',
        fontSize: '14px',
        borderBottom: '1px solid #f5f5f5'
      }
    },
    muiTopToolbarProps: {
      sx: {
        background: '#ffff',
        borderBottom: '1px solid #f0f0f0',
        padding: '16px'
      }
    },
    muiBottomToolbarProps: {
      sx: {
        background: '#ffff',
        borderTop: '1px solid #f0f0f0'
      }
    },
    muiSkeletonProps: {
      animation: 'wave'
    },
    muiLinearProgressProps: {
      color: 'success'
    },
    muiCircularProgressProps: {
      color: 'success'
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      variant: 'outlined',
      size: 'medium'
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: '5px' }}>
        <Tooltip title="Edit Camera">
          <IconButton
            onClick={() => handleEditCamera(row.original)}
            color="primary"
            aria-label="edit"
            sx={{
              fontSize: '14px',
              lineHeight: '17px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              },
              '&:focus': {
                outline: 'none'
              }
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Camera">
          <IconButton
            color="error"
            onClick={() => handleConfirmDialogOpen(row.original._id)}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: '1rem',
          p: '4px',
          alignItems: 'center'
        }}
      >
        <AddCameraButton />

        <Tooltip title="Refresh Data">
          <IconButton
            onClick={fetchCameras}
            color="success"
            disabled={loading}
            sx={{
              borderRadius: '8px',
              backgroundColor: 'rgba(68, 190, 84, 0.08)',
              '&:hover': {
                backgroundColor: 'rgba(68, 190, 84, 0.12)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>

        {table.getSelectedRowModel().rows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              const selectedRows = table.getSelectedRowModel().rows;
              const selectedCameraIds = selectedRows.map((item) => item.original._id);
              console.log('Delete selected cameras:', selectedCameraIds);
            }}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 6px rgba(211, 47, 47, 0.1)',
              '&:hover': {
                boxShadow: '0 6px 10px rgba(211, 47, 47, 0.2)'
              }
            }}
          >
            Delete Selected
          </Button>
        )}
      </Box>
    )
  });

  return (
    <Grid container spacing={1}>
      <SideBar selField="videos" />
      <Grid item md={12} sx={mainContentStyle}>
        {/* Store Name Header */}
        <div className="flex items-center mb-5 -mt-10 pl-2">
          <h1 className="text-2xl font-bold text-gray-800">{storeName}</h1>
        </div>
        {/* Camera Table */}
        <div className="-mt-2">
          <MaterialReactTable table={table} />
        </div>

        {/* AI Configuration Modal */}
        <Modal open={openAnnotate} onClose={handleCloseAnnotate} fullScreen>
          <div className="flex h-full w-full bg-slate-200 p-4 overflow-auto">
            <div className="w-[100%] pr-2">
              <ImageAnnotationTool cameraImage={cameraImage} cameraId={selectedDevice} handleCloseAnnotate={handleCloseAnnotate} />
            </div>

            <div className="w-1/4 pr-10">
              <MiddleController
                handleCloseAnnotate={handleCloseAnnotate}
                cameraId={selectedDevice}
                deviceName={selectedDevice}
                cameraAddress={''}
              />
            </div>
          </div>
        </Modal>

        {/* Confirmation Dialog for Deletion */}
        <Dialog
          open={confirmDialogOpen}
          onClose={handleConfirmDialogClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', pt: 3, pb: 1 }}>Confirm Deletion</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <DialogContentText sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(33,43,54,0.7)' }}>
              Are you sure you want to delete this camera? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Button
              onClick={handleConfirmDialogClose}
              color="primary"
              sx={{
                borderRadius: '8px',
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCameraDeletion}
              color="error"
              variant="contained"
              disabled={deletionInProgress}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 4px 6px rgba(211, 47, 47, 0.1)',
                '&:hover': {
                  boxShadow: '0 6px 10px rgba(211, 47, 47, 0.2)'
                }
              }}
            >
              {deletionInProgress ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={Boolean(deletionSuccess) || Boolean(apiError)}
          autoHideDuration={6000}
          onClose={() => {
            setDeletionSuccess(null);
            setApiError(null);
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {deletionSuccess ? (
            <Alert
              severity="success"
              onClose={() => setDeletionSuccess(null)}
              sx={{
                width: '100%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                '& .MuiAlert-icon': {
                  color: '#44BE54'
                }
              }}
            >
              {deletionSuccess}
            </Alert>
          ) : apiError ? (
            <Alert
              severity="error"
              onClose={() => setApiError(null)}
              sx={{
                width: '100%',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                '& .MuiAlert-icon': {
                  color: '#F44336'
                }
              }}
            >
              {apiError}
            </Alert>
          ) : null}
        </Snackbar>
      </Grid>
    </Grid>
  );
};

export default CameraPage;
